import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

interface Slide {
  text: string;
  navPath: string | null;
  tabName: string | null;
}

const SLIDES: Slide[] = [
  {
    text: "Thank you for testing my movie recommendation app! I created this so Krista and I wouldn't have to spend so much time trying to find something to watch for date night.",
    navPath: null,
    tabName: null,
  },
  {
    text: "Start here! In Settings you can set options for movies to include or exclude in your recommendations — this can be done based on MPAA ratings, genres, and language. You can also set your current streaming providers (Netflix, Disney+, etc.) to ensure the app returns recommendations available to you.",
    navPath: '/settings',
    tabName: 'Settings',
  },
  {
    text: "In the Search section you can search for movies to rate or add to Watchlists.",
    navPath: '/search',
    tabName: 'Search',
  },
  {
    text: "In the My Ratings section you can view and edit ratings you've previously entered.",
    navPath: '/ratings',
    tabName: 'My Ratings',
  },
  {
    text: "In the Recommendations section you can see movie recommendations based on your rated movies. It currently just pulls similar movies from a database, but eventually I hope to incorporate some machine learning to refine them. You can reject the recommendations, rate them, or add them to a watchlist.",
    navPath: '/suggestions',
    tabName: 'Recommendations',
  },
  {
    text: "In the Watchlist section you can view and edit watchlists of movies you've saved.",
    navPath: '/watchlist',
    tabName: 'Watchlist',
  },
];

// --- Styled components ---

const Overlay = styled.div`
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Card = styled.div`
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
  width: 100%;
  max-width: 480px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TourLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-accent);
`;

const SlideCount = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-muted);
`;

const TabIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.82rem;
  color: var(--color-text-secondary);
`;

const TabChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: var(--color-accent-subtle);
  color: var(--color-nav-link-active);
  border: 1px solid var(--color-accent);
  border-radius: 7px;
  padding: 0.25rem 0.65rem;
  font-size: 0.82rem;
  font-weight: 600;
`;

const SlideText = styled.p`
  font-size: 1rem;
  line-height: 1.65;
  color: var(--color-text);
  margin: 0;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.25rem;
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const Dot = styled.div<{ $active: boolean }>`
  width: ${p => p.$active ? '18px' : '7px'};
  height: 7px;
  border-radius: 4px;
  background: ${p => p.$active ? 'var(--color-accent)' : 'var(--color-border)'};
  transition: width 0.2s, background 0.2s;
`;

const OkButton = styled.button`
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1.4rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.88;
  }
`;

// Injected CSS for the nav highlight animation
const HIGHLIGHT_STYLE_ID = 'tutorial-nav-highlight-style';
const HIGHLIGHT_CLASS = 'tutorial-nav-highlight';

function injectHighlightStyle() {
  if (document.getElementById(HIGHLIGHT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HIGHLIGHT_STYLE_ID;
  style.textContent = `
    @keyframes tutorialNavPulse {
      0%, 100% { box-shadow: 0 0 0 2px var(--color-accent, #6366f1); }
      50%       { box-shadow: 0 0 0 4px var(--color-accent, #6366f1), 0 0 14px rgba(99,102,241,0.45); }
    }
    [data-nav-path].${HIGHLIGHT_CLASS} {
      animation: tutorialNavPulse 1.3s ease-in-out infinite;
      border-radius: 8px;
    }
  `;
  document.head.appendChild(style);
}

function removeHighlightStyle() {
  document.getElementById(HIGHLIGHT_STYLE_ID)?.remove();
}

function clearNavHighlight() {
  document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach(el =>
    el.classList.remove(HIGHLIGHT_CLASS)
  );
}

function setNavHighlight(navPath: string | null) {
  clearNavHighlight();
  if (!navPath) return;
  const el = document.querySelector(`[data-nav-path="${navPath}"]`);
  el?.classList.add(HIGHLIGHT_CLASS);
}

// --- Component ---

interface TutorialModalProps {
  onClose: () => void;
}

const TutorialModal: React.FC<TutorialModalProps> = ({ onClose }) => {
  const [index, setIndex] = useState(0);
  const slide = SLIDES[index];
  const isLast = index === SLIDES.length - 1;

  useEffect(() => {
    injectHighlightStyle();
    return () => {
      removeHighlightStyle();
      clearNavHighlight();
    };
  }, []);

  useEffect(() => {
    setNavHighlight(slide.navPath);
  }, [index, slide.navPath]);

  const advance = () => {
    if (isLast) {
      onClose();
    } else {
      setIndex(i => i + 1);
    }
  };

  return ReactDOM.createPortal(
    <Overlay onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <Card>
        <CardHeader>
          <TourLabel>Welcome Tour</TourLabel>
          <SlideCount>{index + 1} of {SLIDES.length}</SlideCount>
        </CardHeader>

        {slide.tabName && (
          <TabIndicator>
            <span>Look at the</span>
            <TabChip>{slide.tabName}</TabChip>
            <span>tab in the nav bar above</span>
          </TabIndicator>
        )}

        <SlideText>{slide.text}</SlideText>

        <CardFooter>
          <Dots>
            {SLIDES.map((_, i) => (
              <Dot key={i} $active={i === index} />
            ))}
          </Dots>
          <OkButton onClick={advance}>
            {isLast ? 'Done' : 'OK'}
          </OkButton>
        </CardFooter>
      </Card>
    </Overlay>,
    document.body
  );
};

export default TutorialModal;
